/**
 * Iter-191 · Theme provider (light / dark / system).
 *
 * Simple, dependency-free implementation:
 *   • Reads persisted choice from `localStorage.zenith_theme`
 *   • Falls back to `system` (follows OS preference)
 *   • Applies to <html> by toggling the `.dark` class (Tailwind convention),
 *     which the existing `:root` + `.dark` CSS variables in index.css pick up.
 *   • Emits a `themechange` window event so any listener can react.
 *
 * Iter-193 · Brand accent palette — tenants can override `--accent` +
 * `--ring` with any hex colour via Settings → Appearance. `applyAccent`
 * converts hex → HSL and pushes it as CSS variables, so every button,
 * link, focus-ring and highlight adopts the tenant's brand colour on
 * top of the light/dark base theme.
 */
const KEY = "zenith_theme";
const ACCENT_KEY = "zenith_accent";
const VALID = ["light", "dark", "system"];

function _effective(mode) {
  if (mode === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return mode === "dark" ? "dark" : "light";
}

function _apply(mode) {
  const effective = _effective(mode);
  document.documentElement.classList.toggle("dark", effective === "dark");
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = effective; // hint for scrollbars, form controls
}

export function getTheme() {
  const v = (typeof localStorage !== "undefined" && localStorage.getItem(KEY)) || "system";
  return VALID.includes(v) ? v : "system";
}

export function setTheme(mode) {
  if (!VALID.includes(mode)) mode = "system";
  try { localStorage.setItem(KEY, mode); } catch { /* private mode */ }
  _apply(mode);
  window.dispatchEvent(new CustomEvent("themechange", { detail: { mode, effective: _effective(mode) } }));
}

export function initTheme() {
  _apply(getTheme());
  // Follow the OS if the user picked "system"
  try {
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      if (getTheme() === "system") _apply("system");
    });
  } catch { /* older browsers */ }
}

// Run immediately on module import so there is no flash-of-wrong-theme.
if (typeof window !== "undefined") initTheme();

// ─────────────────────────────────────────────────────────────────────────
// Iter-193 · Brand accent palette
// ─────────────────────────────────────────────────────────────────────────

/** Convert #RRGGBB → "H S% L%" string that CSS `hsl(var(--accent))` accepts. */
export function hexToHslString(hex) {
  const h = String(hex || "").trim().replace(/^#/, "");
  if (!/^([0-9a-f]{6}|[0-9a-f]{3})$/i.test(h)) return null;
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      case b: hh = (r - g) / d + 4; break;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Curated brand palette — the same list appears in Settings → Appearance. */
export const ACCENT_PRESETS = [
  { key: "emergent",  name: "Emergent Orange", hex: "#F97316" },
  { key: "blue",      name: "Founder Blue",    hex: "#2563EB" },
  { key: "indigo",    name: "Indigo",          hex: "#4F46E5" },
  { key: "purple",    name: "Purple",          hex: "#7C3AED" },
  { key: "emerald",   name: "Emerald",         hex: "#059669" },
  { key: "teal",      name: "Teal",            hex: "#0D9488" },
  { key: "rose",      name: "Rose",            hex: "#E11D48" },
  { key: "amber",     name: "Amber",           hex: "#D97706" },
  { key: "slate",     name: "Slate",           hex: "#334155" },
];

const DEFAULT_ACCENT_HEX = "#F97316";  // Emergent orange — matches index.css

export function getAccent() {
  try {
    const raw = localStorage.getItem(ACCENT_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* private mode */ }
  return { hex: DEFAULT_ACCENT_HEX, source: "default" };
}

/** Push `hex` to CSS as `--accent` + `--ring`; also drops `--brand-orange`
 * so gradients (`bg-logo-gradient`) blend with the tenant's brand.
 * Iter-194 · Auto-contrast — compute WCAG-style relative luminance and
 * flip `--accent-foreground` to near-black or near-white so text stays
 * legible on any accent (light lemon → black text, dark navy → white). */
export function applyAccent(hex) {
  const hsl = hexToHslString(hex);
  if (!hsl) return false;
  const root = document.documentElement;
  root.style.setProperty("--accent", hsl);
  root.style.setProperty("--ring", hsl);
  root.style.setProperty("--brand-orange", hsl);
  root.dataset.accentHex = hex;
  // ── Auto-contrast ────────────────────────────────────────────────
  const h = hex.replace(/^#/, "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const chan = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const R = chan(parseInt(full.slice(0, 2), 16));
  const G = chan(parseInt(full.slice(2, 4), 16));
  const B = chan(parseInt(full.slice(4, 6), 16));
  const L = 0.2126 * R + 0.7152 * G + 0.0722 * B;   // relative luminance
  // Threshold ≈ 0.5 gives the best perceptual split; use near-black /
  // near-white (not pure) so it still matches the design tokens.
  const fg = L > 0.5 ? "220 25% 10%" : "0 0% 100%";
  root.style.setProperty("--accent-foreground", fg);
  root.style.setProperty("--primary-foreground", fg);   // buttons share it
  root.dataset.accentLuma = L.toFixed(3);
  return true;
}

export function setAccent(hex, source = "user") {
  const ok = applyAccent(hex);
  if (!ok) return false;
  try { localStorage.setItem(ACCENT_KEY, JSON.stringify({ hex, source })); } catch {}
  window.dispatchEvent(new CustomEvent("accentchange", { detail: { hex, source } }));
  return true;
}

export function resetAccent() {
  try { localStorage.removeItem(ACCENT_KEY); } catch {}
  applyAccent(DEFAULT_ACCENT_HEX);
  window.dispatchEvent(new CustomEvent("accentchange", { detail: { hex: DEFAULT_ACCENT_HEX, source: "default" } }));
}

// Apply persisted accent on boot too (before first paint)
if (typeof window !== "undefined") {
  const a = getAccent();
  if (a?.hex) applyAccent(a.hex);
}
